import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, PencilSimple, Trash, WarningCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import VatRateSelect from '../components/VatRateSelect';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';
import RecordLossButton from '../components/RecordLossButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Keys = () => {
  const confirm = useConfirm();
  const [keys, setKeys] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    characteristics: '',
    purchase_price: '',
    sale_price: '',
    stock_quantity: '',
    alert_threshold: '10',
    vat_rate: 0.085,
    supplier: '',
  });

  useEffect(() => {
    fetchKeys();
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/suppliers`, { withCredentials: true });
      setSuppliers(r.data);
    } catch (e) { /* ignore */ }
  };

  const fetchKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/keys`, {
        withCredentials: true,
      });
      setKeys(response.data);
    } catch (error) {
      console.error('Error fetching keys:', error);
      toast.error('Erreur lors du chargement des clés');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingKey) {
        await axios.put(`${API_URL}/api/keys/${editingKey.id}`, {
          ...formData,
          purchase_price: parseFloat(formData.purchase_price),
          sale_price: parseFloat(formData.sale_price),
          stock_quantity: parseInt(formData.stock_quantity),
          alert_threshold: parseInt(formData.alert_threshold)
        }, { withCredentials: true });
        toast.success('Clé mise à jour');
      } else {
        await axios.post(`${API_URL}/api/keys`, {
          ...formData,
          purchase_price: parseFloat(formData.purchase_price),
          sale_price: parseFloat(formData.sale_price),
          stock_quantity: parseInt(formData.stock_quantity),
          alert_threshold: parseInt(formData.alert_threshold)
        }, { withCredentials: true });
        toast.success('Clé créée');
      }
      setShowDialog(false);
      resetForm();
      fetchKeys();
    } catch (error) {
      console.error('Error saving key:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer cette clé ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/keys/${id}`, { withCredentials: true });
      toast.success('Clé supprimée');
      fetchKeys();
    } catch (error) {
      console.error('Error deleting key:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      characteristics: '',
      purchase_price: '',
      sale_price: '',
      stock_quantity: '',
      alert_threshold: '10',
      vat_rate: 0.085,
      supplier: '',
    });
    setEditingKey(null);
  };

  const openEditDialog = (key) => {
    setEditingKey(key);
    setFormData({
      name: key.name,
      characteristics: key.characteristics,
      purchase_price: key.purchase_price.toString(),
      sale_price: key.sale_price.toString(),
      stock_quantity: key.stock_quantity.toString(),
      alert_threshold: key.alert_threshold.toString(),
      vat_rate: key.vat_rate !== undefined ? key.vat_rate : 0.085,
      supplier: key.supplier || '',
    });
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="keys-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Gestion des clés</h1>
            <p className="text-slate-600">Gérez vos types de clés et leur stock</p>
          </div>
          <Button
            onClick={openCreateDialog}
            data-testid="create-key-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Ajouter un type de clé
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom ou caractéristiques..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-keys-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Caractéristiques</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix achat</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix vente</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Stock</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Seuil alerte</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Fournisseur</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys
                    .filter(key => !searchQuery || 
                      key.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      key.characteristics.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((key) => (
                    <tr key={key.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{key.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{key.characteristics}</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{key.purchase_price.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{key.sale_price.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={key.stock_quantity <= key.alert_threshold ? 'text-red-600 font-medium' : 'text-slate-900'}>
                            {key.stock_quantity}
                          </span>
                          {key.stock_quantity <= key.alert_threshold && (
                            <WarningCircle size={16} className="text-orange-500" weight="fill" />
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{key.alert_threshold}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{key.supplier || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openEditDialog(key)}
                          data-testid={`edit-key-${key.id}`}
                          className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-1"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <RecordLossButton
                          productId={key.id}
                          productType="key"
                          productName={key.name}
                          currentStock={key.stock_quantity}
                          onDone={fetchKeys}
                        />
                        <button
                          onClick={() => handleDelete(key.id)}
                          data-testid={`delete-key-${key.id}`}
                          className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md ml-1"
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {keys.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-12 text-center text-slate-500">
                        Aucun type de clé enregistré
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="key-dialog">
            <DialogHeader>
              <DialogTitle>{editingKey ? 'Modifier' : 'Ajouter'} un type de clé</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="key-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Caractéristiques</label>
                <Input
                  value={formData.characteristics}
                  onChange={(e) => setFormData({ ...formData, characteristics: e.target.value })}
                  data-testid="key-characteristics-input"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prix d&apos;achat (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    data-testid="key-purchase-price-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prix de vente (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.sale_price}
                    onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                    data-testid="key-sale-price-input"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantité en stock</label>
                  <Input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    data-testid="key-stock-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seuil d&apos;alerte</label>
                  <Input
                    type="number"
                    value={formData.alert_threshold}
                    onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                    data-testid="key-alert-input"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taux de TVA</label>
                <VatRateSelect
                  value={formData.vat_rate}
                  onChange={(v) => setFormData({ ...formData, vat_rate: v })}
                  testId="key-vat-rate-select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur</label>
                <select
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  data-testid="key-supplier-input"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white"
                >
                  <option value="">— Aucun —</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                  {formData.supplier && !suppliers.find((s) => s.name === formData.supplier) && (
                    <option value={formData.supplier}>{formData.supplier} (ancien)</option>
                  )}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Vous pouvez ajouter de nouveaux fournisseurs dans l&apos;onglet <strong>Fournisseurs</strong>.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  data-testid="cancel-key-btn"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-key-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  {editingKey ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Keys
